// Copyright (c) Docugami, Inc. All rights reserved.

import { Checkbox, Dialog, DialogFooter, DirectionalHint, Icon, PrimaryButton, Stack, TooltipHost } from "@fluentui/react";
import { DocSetPicker } from "components/cluster_management/presentation/DocSetPicker";
import { DgInputBox, DisabledCss } from "components/common/DgCommonStyledComponents";
import { dgFontFamily, SectionLabel } from "components/common/Fonts";
import { emptyGray } from "components/shell/presentation/AppStyles";
import { useKeyPressHandler } from "hooks/KeyPressHandler";
import { NavigationType, useNavigationService } from "hooks/navigationService";
import { useWorkPathReportLabels, useWorkPathTypeName } from "hooks/useWorkPathTypeName";
import { DocSetCollectionType, NamedResource, WorkPath, WorkPathType } from "networking/WorkPathApi";
import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { useGetSuggestedReportStatusQuery } from "store/reports/reportsSlice";
import { useCloneWorkPathsMutation, useCreateWorkPathMutation } from "store/work_path/workPathSlice";
import styled from 'styled-components';
import { getClickEventIdForAutomationId } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { isValidDocument } from "utils/AssistedAuthoringUtils";
import { AutomationId } from "utils/Automation";
import { getNumberOfChunkSuggestionsForDemoMode, setShowFirstOneClickWorkPathViewingUx } from "utils/localStorageHelpers";
import { CloningSlidingPanel } from "./CloningSlidingPanel";
import { DocsetDocumentPicker } from "./DocsetDocumentPicker";
import { isReportRequired } from "./WorkPathsHelper";

const NameContainer = styled.div`
    margin-bottom: 40px;
`;

const ContentRoot = styled.div<{ busy?: boolean }>`
    ${props => !!props.busy && DisabledCss}
    font-family: ${dgFontFamily};
`

const StyledDgInputBox = styled(DgInputBox)`
    width: 100%;
    padding: 0px;
    margin-top: 0;
    border-color: ${emptyGray};
    font-weight: 300;
`;

const StyledDocSetPicker = styled(DocSetPicker)`
    max-height: 40vh;
`;

const StyledPrimaryButton = styled(PrimaryButton)`
    margin-top: 30px;
    margin-right: 5px;
    margin-left: auto;
`
const StyledDialogFooter = styled(DialogFooter)`
    margin-top: 0;
`

interface WorkPathCreationModalProps {
    collectionFilter: DocSetCollectionType;
    type: WorkPathType;
    docSetId?: string;
}

export const WorkPathCreationModal = (props: WorkPathCreationModalProps) => {
    const [selectedClusterId, setSelectedClusterId] = useState(props.docSetId ?? '');
    const [name, setName] = useState('');
    const [resources, setResources] = useState<{ [id: string]: string }>();
    const [generateWithSuggestedItems, setGenerateWithSuggestedItems] = useState(false);
    const [sourceWorkPathId, setSourceWorkPathId] = useState<string | undefined>();

    const { navToWorkPathPage } = useNavigationService();

    const [triggerSave, saveData] = useCreateWorkPathMutation();
    const [triggerClone] = useCloneWorkPathsMutation();

    const workPathTypeName = useWorkPathTypeName(props.type);
    const history = useHistory();

    const canConfirm = useMemo(() => !!name.trim() && !!selectedClusterId, [name, selectedClusterId]);

    const onConfirm = useCallback(async () => {
        if (!canConfirm) {
            return;
        }

        const trimmedName = name.trim();
        if (!trimmedName) {
            return;
        }

        Telemetry.trackNamedEvent(getClickEventIdForAutomationId(AutomationId.WorkPathChooseDocSetAndNameNextButton), { oneClick: generateWithSuggestedItems })

        let newWorkPath: WorkPath;
        if (!!sourceWorkPathId) {
            newWorkPath = await triggerClone({
                sourceWorkPathId,
                workPath: {
                    name: trimmedName,
                    clusterId: selectedClusterId,
                    workPathType: props.type,
                }
            }).unwrap();
        }
        else {
            const maxEntries = generateWithSuggestedItems ? getNumberOfChunkSuggestionsForDemoMode() : undefined;
            newWorkPath = (await triggerSave({
                docsetId: selectedClusterId,
                name: name,
                type: props.type,
                maxSuggestedEntries: maxEntries,
                resources: resources
            }).unwrap())?.workPath;

            if (!newWorkPath) {
                throw new Error("Failed to create the work path.");
            }

            if (!!maxEntries) {
                setShowFirstOneClickWorkPathViewingUx(newWorkPath.id, true);
            }
        }

        navToWorkPathPage(NavigationType.ReplaceCurrentPage, newWorkPath.id);
    }, [canConfirm, name, selectedClusterId, props.type, sourceWorkPathId, navToWorkPathPage, resources, generateWithSuggestedItems, triggerClone, triggerSave]);

    const onCancel = () => {
        history.goBack();
    }

    function getFooter() {
        switch (props.type) {
            case WorkPathType.AssistedAuthoring:
                return <DocsetDocumentPicker
                    docSetId={selectedClusterId}
                    onDocumentSelected={documentId => setResources(prev => ({ ...prev, [NamedResource.DocumentId]: documentId }))}
                    documentsFilter={document => isValidDocument(document)}
                    dropdownLabel="Choose a document for your project:" />;
            default:
                return <ReportResourcePickers
                    type={props.type}
                    docsetId={selectedClusterId}
                    generateWithSuggestedItems={generateWithSuggestedItems}
                    setGenerateWithSuggestedItems={setGenerateWithSuggestedItems}
                    sourceWorkPathId={sourceWorkPathId}
                    setSourceWorkPathId={setSourceWorkPathId} />;
        }
    }

    useKeyPressHandler('Enter', () => onConfirm());
    const busy = saveData.isLoading;

    return (
        <Dialog
            styles={{
                root: {
                    whiteSpace: 'break-spaces',
                    height: '100%',
                    cursor: busy ? 'wait' : 'default'
                },
                main: {
                    marginTop: '40px',
                }
            }}
            onDismiss={onCancel}
            hidden={busy}
            minWidth='66%'
            modalProps={{
                isBlocking: true,
                isDarkOverlay: false,
            }}>
            <ContentRoot busy={busy}>
                <NameContainer>
                    <SectionLabel>Choose a name for your {workPathTypeName.toLowerCase()}: *</SectionLabel>
                    <StyledDgInputBox autoFocus={true} value={name} onChange={e => setName(e.target.value)} />
                </NameContainer>
                <div>
                    <SectionLabel>Choose a doc set for your {workPathTypeName.toLowerCase()}: *</SectionLabel>
                    <StyledDocSetPicker
                        onSelected={setSelectedClusterId}
                        targetDocSet={selectedClusterId}
                        collectionFilter={props.collectionFilter}
                        workPathType={props.type} />
                </div>
                {getFooter()}
                <StyledDialogFooter>
                    <StyledPrimaryButton
                        data-automation-id={AutomationId.WorkPathChooseDocSetAndNameNextButton}
                        disabled={!canConfirm}
                        onClick={onConfirm}
                        text={"Start"} />
                </StyledDialogFooter>
            </ContentRoot>
        </Dialog>
    );
}


interface ReportResourcePickersProps {
    type: WorkPathType;
    docsetId: string;
    generateWithSuggestedItems: boolean;
    sourceWorkPathId: string | undefined;
    setGenerateWithSuggestedItems: React.Dispatch<React.SetStateAction<boolean>>;
    setSourceWorkPathId: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const ReportResourcePickers = (props: ReportResourcePickersProps) => {
    const reportLabels = useWorkPathReportLabels(props.type);
    const isOneClickEnabled = isReportRequired(props.type);

    const { data: hasSuggestedReport } = useGetSuggestedReportStatusQuery({ clusterId: props.docsetId }, { skip: !isOneClickEnabled || !props.docsetId });

    return (
        <Stack tokens={{ childrenGap: '10px' }}>
            {isReportRequired(props.type) &&
                <CloningSlidingPanel onSourceWorkPathIdChange={(sourceWorkPathId: string | undefined) => {
                    props.setSourceWorkPathId(sourceWorkPathId);
                }} />}
            {isOneClickEnabled ?
                <Stack horizontal horizontalAlign="start" verticalAlign="center">
                    <Checkbox
                        data-automation-id={AutomationId.WorkPathChooseDocSetAndNameOneClickCheckBox}
                        styles={{
                            label: {
                                fontSize: 18,
                                fontWeight: 600
                            }
                        }}
                        disabled={!hasSuggestedReport || !!props.sourceWorkPathId}
                        checked={!!hasSuggestedReport && props.generateWithSuggestedItems && !props.sourceWorkPathId}
                        label="Generate with suggested items"
                        onChange={() => {
                            if (hasSuggestedReport) {
                                props.setGenerateWithSuggestedItems(prev => {
                                    const newValue = !prev;
                                    Telemetry.trackNamedEvent(`${AutomationId.WorkPathChooseDocSetAndNameOneClickCheckBox}_${newValue ? 'Checked' : 'Unchecked'}`)
                                    return newValue;
                                })
                            }
                        }} />
                    <TooltipHost
                        styles={{
                            root: {
                                width: 14,
                                height: 18,
                                fontSize: 13,
                                marginLeft: 5,
                                textAlign: 'center'
                            }
                        }}
                        directionalHint={DirectionalHint.rightCenter}
                        content={`Let our AI automatically populate your ${reportLabels.titleName.toLowerCase()} with suggested items of interest based on your doc set.`}
                    >
                        <Icon
                            iconName="info"
                            styles={{
                                root: {
                                    fontWeight: 600,
                                    width: 14,
                                    lineHeight: 10
                                }
                            }}
                        />
                    </TooltipHost>
                </Stack>
                :
                null}
        </Stack>
    )
}
